/**
 * Prices are stored and transported as decimal (the format every source feed
 * and the odds pool agree on). The display format is per-locale — decimal
 * almost everywhere, fractional for UK, american for CA/MX. Converting on the
 * widget means one API response serves every locale.
 */
export type OddsFormat = 'decimal' | 'fractional' | 'american'

/**
 * The UK fractional ladder bookmakers actually quote. Converting a decimal to
 * its exact fraction gives prices no bookmaker shows (1.77 → "77/100", a
 * converted moneyline → "20/57"), so a price snaps to the closest rung.
 * Kept unreduced where the market says it that way (4/6, 6/4).
 */
const UK_LADDER = [
  '1/100', '1/66', '1/50', '1/33', '1/25', '1/20', '1/16', '1/14', '1/12', '1/10', '1/9', '1/8',
  '2/15', '1/7', '2/13', '1/6', '2/11', '1/5', '2/9', '1/4', '2/7', '3/10', '1/3', '4/11', '3/8',
  '2/5', '4/9', '1/2', '8/15', '4/7', '8/13', '4/6', '8/11', '4/5', '5/6', '10/11', 'Evs', '21/20',
  '11/10', '6/5', '5/4', '11/8', '6/4', '13/8', '7/4', '15/8', '2/1', '9/4', '5/2', '11/4', '3/1',
  '10/3', '7/2', '4/1', '9/2', '5/1', '11/2', '6/1', '13/2', '7/1', '15/2', '8/1', '17/2', '9/1',
  '10/1', '11/1', '12/1', '14/1', '16/1', '18/1', '20/1', '22/1', '25/1', '28/1', '33/1', '40/1',
  '50/1', '66/1', '80/1', '100/1', '125/1', '150/1', '200/1', '250/1', '300/1', '400/1', '500/1',
  '750/1', '1000/1',
].map((label) => {
  const [n, d] = label === 'Evs' ? [1, 1] : label.split('/').map(Number)
  return { label, decimal: 1 + n / d }
})

/** Decimal → the closest UK ladder price ("7/4", "Evs"); ties go to the shorter price. */
function toFractional(decimal: number): string {
  let best = UK_LADDER[0]
  for (const rung of UK_LADDER) {
    if (Math.abs(rung.decimal - decimal) < Math.abs(best.decimal - decimal) - 1e-9) best = rung
  }
  return best.label
}

/** Decimal → "+150" / "-200". */
function toAmerican(decimal: number): string {
  if (decimal <= 1) return ''
  if (decimal >= 2) return `+${Math.round((decimal - 1) * 100)}`
  return `${Math.round(-100 / (decimal - 1))}`
}

/**
 * Format one decimal price for display. Bad/blank input returns '' so a widget
 * can show a dash rather than "NaN".
 */
export function formatOdds(decimal: number | string | null | undefined, format: OddsFormat): string {
  const value = typeof decimal === 'string' ? Number(decimal) : decimal
  if (value == null || !Number.isFinite(value) || value <= 1) return ''
  switch (format) {
    case 'fractional':
      return toFractional(value)
    case 'american':
      return toAmerican(value)
    default:
      return value.toFixed(2)
  }
}
